/**
 * PaymentsService - HTTP client for the "Pagos" module (migrated from
 * admin/payments_new). The PHP APIs read $_POST (form-urlencoded) and $_GET,
 * so this client sends URLSearchParams bodies and query strings. Some legacy
 * endpoints (billing data, reminders, report published) return raw payloads
 * without a "status" contract, so they are exposed via raw request helpers.
 */
import {
    urlInk,
    endpointPaymentsGetMeta,
    endpointPaymentsGetPayments,
    endpointPaymentsGetUsdByChannel,
    endpointPaymentsPostPayments,
    endpointPaymentsDownload,
    endpointPaymentsDownloadExcel,
    endpointPaymentsGetBillingData,
    endpointPaymentsSendBillingReminder,
    endpointPaymentsSendReportPublished,
    endpointPaymentsChangeSession,
} from "./Endpoints";
import AuthService from "./AuthService";

class PaymentsService {
    async request(endpoint, options = {}) {
        const token = AuthService.getToken();
        const headers = { ...(options.headers || {}) };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${urlInk}${endpoint}`, {
            method: options.method || 'GET',
            headers,
            body: options.body,
        });

        if (!response.ok) {
            throw new Error('Error de conexión con el servidor');
        }

        return response;
    }

    async requestJson(endpoint, options = {}) {
        const response = await this.request(endpoint, options);
        let res;
        try {
            res = await response.json();
        } catch (e) {
            throw new Error('Respuesta no válida del servidor');
        }
        if (!res || res.status !== 'success') {
            const msg = (res && res.msg) ? res.msg : 'Ocurrió un error';
            const error = new Error(msg);
            error.status = res ? res.status : 'error';
            throw error;
        }
        return res;
    }

    buildParams(obj) {
        const params = new URLSearchParams();
        Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (v !== undefined && v !== null && v !== '') {
                params.set(k, v);
            }
        });
        return params.toString();
    }

    formBody(obj) {
        const params = new URLSearchParams();
        Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (v !== undefined && v !== null && v !== '') {
                params.set(k, v);
            }
        });
        return params;
    }

    // ---------------- Meta ----------------
    async getMeta() {
        const res = await this.requestJson(endpointPaymentsGetMeta);
        return res.data;
    }

    // ---------------- Payments data ----------------
    async getPayments(params) {
        const res = await this.requestJson(endpointPaymentsGetPayments, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody(params),
        });
        return res;
    }

    async getUsdByChannel(params) {
        const res = await this.requestJson(endpointPaymentsGetUsdByChannel, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody(params),
        });
        return res.data;
    }

    async postPayments(params) {
        const res = await this.requestJson(endpointPaymentsPostPayments, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody(params),
        });
        return res;
    }

    // ---------------- Downloads ----------------
    async downloadAll(params) {
        const qs = this.buildParams(params);
        const res = await this.requestJson(`${endpointPaymentsDownload}?${qs}`);
        return res.file;
    }

    async downloadAccount(params) {
        const qs = this.buildParams(params);
        const res = await this.requestJson(`${endpointPaymentsDownloadExcel}?${qs}`);
        return res.file;
    }

    // ---------------- Billing data & reminders (raw responses) ----------------
    async getBillingData(account) {
        const response = await this.request(endpointPaymentsGetBillingData, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody({ account }),
        });
        return response.json();
    }

    async sendBillingReminder(account, lang = 'es') {
        const response = await this.request(endpointPaymentsSendBillingReminder, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody({ account, lang }),
        });
        return response.json();
    }

    async sendReportPublished(accounts, period, lang = 'es') {
        const response = await this.request(endpointPaymentsSendReportPublished, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody({ account: JSON.stringify(accounts), period, lang }),
        });
        return response.json();
    }

    // ---------------- "Ver" action: set PHP session for the app ----------------
    async changeSession(editorial) {
        const response = await this.request(endpointPaymentsChangeSession, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: this.formBody({ editorial }),
        });
        return response.text();
    }
}

const PaymentsServiceInstance = new PaymentsService();

export { PaymentsServiceInstance as PaymentsService };

export default PaymentsServiceInstance;